import { useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { PageWithTitle } from 'src/Common/components';
import { usePositions } from '../context';
import { useUpdatePairThreshold } from '../api/usePositions';
import { PredictionPosition, PositionsGroupedByPair } from '../types';

const formatUsd = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPrice = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(4);
};

const formatContracts = (value: number | undefined) => {
  if (value == null) return '-';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const pnlColor = (pnl: number | undefined) => {
  if (pnl == null) return 'inherit';
  if (pnl > 0) return '#4caf50';
  if (pnl < 0) return '#f44336';
  return 'inherit';
};

const platformLabel = (platform: string) =>
  platform === 'polymarket'
    ? 'Poly'
    : platform === 'kalshi'
      ? 'Kalshi'
      : platform;

type SortField = 'pnl' | 'net' | 'makerTna' | 'takerTna';
type SortDir = 'asc' | 'desc';

const PositionCell = ({ position }: { position: PredictionPosition }) => {
  const value = position.avgPrice * position.contracts;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ flex: '1 1 0', minWidth: 0 }}
    >
      <Tooltip title={platformLabel(position.platform)} placement="top">
        <img
          src={`/assets/exchanges/png/100x100/${position.platform}.png`}
          alt={position.platform}
          style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }}
        />
      </Tooltip>
      <Chip
        label={position.side.toUpperCase()}
        size="small"
        color={position.side === 'yes' ? 'success' : 'error'}
        sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20, minWidth: 36 }}
      />
      <Typography
        variant="body2"
        sx={{ fontSize: '0.78rem', color: 'grey.400', whiteSpace: 'nowrap' }}
      >
        {formatPrice(position.avgPrice)}
        <Typography
          component="span"
          sx={{ fontSize: '0.72rem', color: 'grey.600', mx: 0.3 }}
        >
          →
        </Typography>
        {formatPrice(position.currentPrice)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: '#64b5f6', fontWeight: 600 }}
      >
        {formatContracts(position.contracts)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontSize: '0.78rem', color: 'grey.400', whiteSpace: 'nowrap' }}
      >
        {formatUsd(value)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.78rem',
          fontWeight: 600,
          color: pnlColor(position.unrealizedPnl),
          whiteSpace: 'nowrap'
        }}
      >
        {formatUsd(position.unrealizedPnl)}
      </Typography>
      {position.marketUrl && (
        <IconButton
          size="small"
          component="a"
          href={position.marketUrl}
          target="_blank"
          rel="noopener"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ p: 0.3 }}
        >
          <OpenInNewIcon sx={{ fontSize: '0.85rem', color: 'grey.500' }} />
        </IconButton>
      )}
    </Stack>
  );
};

const EmptyPositionCell = () => (
  <Stack direction="row" alignItems="center" sx={{ flex: '1 1 0', minWidth: 0 }}>
    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'grey.700' }}>
      —
    </Typography>
  </Stack>
);

const ThresholdInput = ({
  pairId,
  currentThreshold,
  onUpdate,
}: {
  pairId: string;
  currentThreshold: number;
  onUpdate: (pairId: string, value: number) => void;
}) => {
  const [value, setValue] = useState(currentThreshold > 0 ? String(currentThreshold) : '');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleChange = (newVal: string) => {
    setValue(newVal);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const num = parseFloat(newVal);
      onUpdate(pairId, isNaN(num) ? 0 : num);
    }, 800);
  };

  return (
    <Tooltip title="Max investment threshold — hides from opportunities when reached" placement="top">
      <TextField
        size="small"
        type="number"
        placeholder="no limit"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.6rem', color: 'grey.600' } }}>
              $
            </InputAdornment>
          ),
        }}
        sx={{
          width: 85,
          '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.3, px: 0.3 },
          '& .MuiOutlinedInput-root': { height: 24 },
        }}
      />
    </Tooltip>
  );
};

const PairRow = ({ group, onUpdateThreshold }: { group: PositionsGroupedByPair; onUpdateThreshold?: (pairId: string, value: number) => void }) => {
  const totalPnl = group.totalUnrealizedPnl ?? 0;
  const net = group.netContracts ?? 0;
  const yesPos = group.positions.find(p => p.side === 'yes');
  const noPos = group.positions.find(p => p.side === 'no');

  return (
    <Box
      sx={{
        px: 2,
        py: 0.75,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' }
      }}
    >
      {/* Row 1: Title + Summary */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 500,
            color: 'white',
            flex: 1,
            minWidth: 0
          }}
          noWrap
          title={group.pairName}
        >
          {group.pairName || 'Unknown'}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 2 }}>
          {group.makerTna != null && (
            <Tooltip title="Maker TNA (no fees)" placement="top">
              <Chip
                label={`M ${group.makerTna.toFixed(1)}%`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  fontWeight: 600,
                  borderColor: group.makerTna > 0 ? '#4caf50' : '#f44336',
                  color: group.makerTna > 0 ? '#4caf50' : '#f44336',
                }}
              />
            </Tooltip>
          )}
          {group.takerTna != null && (
            <Tooltip title="Taker TNA (with Kalshi fees)" placement="top">
              <Chip
                label={`T ${group.takerTna.toFixed(1)}%`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  fontWeight: 600,
                  borderColor: group.takerTna > 0 ? '#4caf50' : '#f44336',
                  color: group.takerTna > 0 ? '#4caf50' : '#f44336',
                }}
              />
            </Tooltip>
          )}
          {group.daysToExpiry != null && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.65rem', color: 'grey.500', whiteSpace: 'nowrap' }}
            >
              {group.daysToExpiry}d
            </Typography>
          )}
          {group._id && onUpdateThreshold && (
            <ThresholdInput
              pairId={group._id}
              currentThreshold={group.maxInvestmentThreshold ?? 0}
              onUpdate={onUpdateThreshold}
            />
          )}
          <Chip
            label={formatUsd(group.totalInvested)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 22, borderColor: 'grey.700' }}
          />
          <Chip
            label={formatUsd(totalPnl)}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.65rem',
              height: 22,
              fontWeight: 600,
              borderColor: pnlColor(totalPnl),
              color: pnlColor(totalPnl)
            }}
          />
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: net > 0 ? '#4caf50' : net < 0 ? '#f44336' : 'grey.400',
              ml: 0.5,
              whiteSpace: 'nowrap',
              minWidth: 60,
              textAlign: 'right',
            }}
          >
            {net > 0 ? '+' : ''}{formatContracts(net)} net
          </Typography>
        </Stack>
      </Stack>

      {/* Row 2: YES position | NO position */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {yesPos ? <PositionCell position={yesPos} /> : <EmptyPositionCell />}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.08)' }}
        />
        {noPos ? <PositionCell position={noPos} /> : <EmptyPositionCell />}
      </Stack>
    </Box>
  );
};

const SortButton = ({
  label,
  field,
  activeField,
  activeDir,
  onClick,
}: {
  label: string;
  field: SortField;
  activeField: SortField;
  activeDir: SortDir;
  onClick: (field: SortField) => void;
}) => {
  const isActive = activeField === field;
  const Icon = isActive && activeDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Chip
      label={
        <Stack direction="row" alignItems="center" spacing={0.3}>
          <span>{label}</span>
          <Icon sx={{ fontSize: '0.75rem', opacity: isActive ? 1 : 0.3 }} />
        </Stack>
      }
      size="small"
      variant={isActive ? 'filled' : 'outlined'}
      onClick={() => onClick(field)}
      sx={{
        fontSize: '0.65rem',
        height: 22,
        cursor: 'pointer',
        ...(isActive && { backgroundColor: 'rgba(255,255,255,0.1)' }),
      }}
    />
  );
};

const HeaderRow = ({
  sortField,
  sortDir,
  onSort,
}: {
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) => (
  <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
    {/* Title row header */}
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.25}>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.68rem', color: 'grey.600' }}
      >
        Market
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="caption"
          sx={{ fontSize: '0.68rem', color: 'grey.600', mr: 0.5 }}
        >
          Sort:
        </Typography>
        <SortButton label="P&L" field="pnl" activeField={sortField} activeDir={sortDir} onClick={onSort} />
        <SortButton label="Maker TNA" field="makerTna" activeField={sortField} activeDir={sortDir} onClick={onSort} />
        <SortButton label="Taker TNA" field="takerTna" activeField={sortField} activeDir={sortDir} onClick={onSort} />
        <SortButton label="Net" field="net" activeField={sortField} activeDir={sortDir} onClick={onSort} />
      </Stack>
    </Stack>
    {/* Position row header */}
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.68rem', color: 'grey.600', flex: '1 1 0' }}
      >
        YES Position
      </Typography>
      <Box sx={{ width: 17 }} />
      <Typography
        variant="caption"
        sx={{ fontSize: '0.68rem', color: 'grey.600', flex: '1 1 0' }}
      >
        NO Position
      </Typography>
    </Stack>
  </Box>
);

export const PositionsPage = () => {
  const [tab, setTab] = useState(0);
  const [sortField, setSortField] = useState<SortField>('pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const { groups, unmatched, stats, isConnected, triggerSync, isSyncing } =
    usePositions();
  const { mutate: updateThreshold } = useUpdatePairThreshold();

  const handleUpdateThreshold = useCallback((pairId: string, value: number) => {
    updateThreshold(
      { pairId, threshold: value },
      {
        onSuccess: () => setSnackbar({ open: true, message: 'Threshold updated', severity: 'success' }),
        onError: () => setSnackbar({ open: true, message: 'Failed to update threshold', severity: 'error' }),
      }
    );
  }, [updateThreshold]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedGroups = useMemo(() => {
    const sorted = [...groups];
    const mult = sortDir === 'desc' ? -1 : 1;
    sorted.sort((a, b) => {
      if (sortField === 'pnl') {
        return mult * ((a.totalUnrealizedPnl ?? 0) - (b.totalUnrealizedPnl ?? 0));
      }
      if (sortField === 'makerTna') {
        return mult * ((a.makerTna ?? 0) - (b.makerTna ?? 0));
      }
      if (sortField === 'takerTna') {
        return mult * ((a.takerTna ?? 0) - (b.takerTna ?? 0));
      }
      return mult * (Math.abs(a.netContracts ?? 0) - Math.abs(b.netContracts ?? 0));
    });
    return sorted;
  }, [groups, sortField, sortDir]);

  const unmatchedPositions = unmatched.flatMap(g => g.positions);

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontSize: '1.1rem' }}
            >
              Positions
            </Typography>
            <FiberManualRecordIcon
              sx={{ fontSize: 8, color: isConnected ? '#4caf50' : '#f44336' }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {stats && (
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`Open: ${stats.open}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Invested: ${formatUsd(stats.totalInvested)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`P&L: ${formatUsd(stats.totalUnrealizedPnl)}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: pnlColor(stats.totalUnrealizedPnl),
                    color: pnlColor(stats.totalUnrealizedPnl)
                  }}
                />
              </Stack>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={triggerSync}
              disabled={isSyncing}
            >
              Sync
            </Button>
          </Stack>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 1,
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0,
              textTransform: 'none',
              fontSize: '0.82rem'
            },
            '& .MuiTabs-indicator': { height: 2 }
          }}
        >
          <Tab label={`Matched (${groups.length})`} />
          <Tab label={`Unmatched (${unmatchedPositions.length})`} />
        </Tabs>

        {tab === 0 && (
          <Box
            sx={{
              background: 'background.paper',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <HeaderRow sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            {sortedGroups.length === 0 ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Typography color="text.secondary" variant="body2">
                  No matched positions
                </Typography>
              </Box>
            ) : (
              sortedGroups.map(group => (
                <PairRow key={group._id!} group={group} onUpdateThreshold={handleUpdateThreshold} />
              ))
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box
            sx={{
              background: 'background.paper',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            {unmatchedPositions.length === 0 ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Typography color="text.secondary" variant="body2">
                  No unmatched positions
                </Typography>
              </Box>
            ) : (
              unmatched.map((group, i) => (
                <PairRow key={group._id ?? `unmatched-${i}`} group={group} />
              ))
            )}
          </Box>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageWithTitle>
  );
};
